import React, { memo } from 'react';
import { useTranslation } from 'react-i18next';

import { BADGE_POLL_INTERVAL_MS, useGroupsOverview } from 'core/hooks';

import * as S from './Communicator.styled';

interface IGroupsColumnProps {
  query: string;
  selectedGroupId: string;
  onQuery: (query: string) => void;
  onSelect: (groupId: string) => void;
}

/**
 * Left pane: all groups with the command-center signals — amber bubble =
 * conversations waiting for a reply, red bubble = overdue tasks. The search
 * query lives in the URL (`?q=`), so a shared link restores the filter.
 */
export const GroupsColumn = memo(
  ({ query, selectedGroupId, onQuery, onSelect }: IGroupsColumnProps) => {
    const { t } = useTranslation();
    const { groups, loading } = useGroupsOverview(
      query,
      false,
      BADGE_POLL_INTERVAL_MS,
    );

    return (
      <S.Pane>
        <S.PaneHeader>{t('pages.communicator.groupsLabel')}</S.PaneHeader>
        <S.PaneSearch
          type="search"
          value={query}
          placeholder={t('pages.communicator.searchGroups')}
          onChange={(e) => onQuery(e.target.value)}
        />
        <S.PaneList>
          {groups.map((group) => (
            <S.Row
              key={group.groupId}
              type="button"
              active={group.groupId === selectedGroupId}
              onClick={() => onSelect(group.groupId)}
            >
              <S.RowMain>
                <b>{group.name}</b>
                <span>{group.course}</span>
              </S.RowMain>
              {group.unansweredCount > 0 && (
                <S.WarnBubble title={t('pages.dashboard.col.unanswered')}>
                  {group.unansweredCount}
                </S.WarnBubble>
              )}
              {group.overdueTasksCount > 0 && (
                <S.DangerBubble title={t('pages.dashboard.kpiOverdueTasks')}>
                  {group.overdueTasksCount}
                </S.DangerBubble>
              )}
            </S.Row>
          ))}
          {!loading && !groups.length && (
            <S.Muted>{t('pages.communicator.groupsEmpty')}</S.Muted>
          )}
        </S.PaneList>
      </S.Pane>
    );
  },
);

GroupsColumn.displayName = 'GroupsColumn';
